/* eslint-disable jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */
import './CheckboxField.scss';

import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Checkbox from '@/common/components/atoms/Checkbox/Checkbox';
import HtmlElement from '@/common/components/atoms/HtmlElement/HtmlElement';

import FieldErrors from '../FieldErrors/FieldErrors';

const CheckboxField = ({
  checked,
  className,
  context,
  decoration,
  disabled,
  errors,
  errorsIcon,
  font,
  icon,
  id,
  labelContent,
  labelOnClick,
  letterSpacing,
  lineHeight,
  name,
  onBlur,
  onChange,
  position,
  readOnly,
  required,
  size,
  textTransform,
  title,
  type,
  value,
  weight,
}) => {
  const [internalChecked, setInternalChecked] = useState(checked);

  const classNames = useMemo(
    () =>
      [
        'checkbox-field',
        internalChecked ? 'checkbox-field--checked' : null,
        errors && errors.filter((val) => val).length
          ? 'checkbox-field--error'
          : null,
        `checkbox-field--${position}`,
        readOnly ? 'checkbox-field--readonly' : null,
        required ? 'checkbox-field--required' : null,
        disabled ? 'checkbox-field--disabled' : null,
        className,
      ]
        .filter((val) => val)
        .join(' '),
    [
      internalChecked,
      className,
      disabled,
      errors,
      position,
      readOnly,
      required,
    ],
  );

  useEffect(() => setInternalChecked(checked), [checked]);

  const handleChange = useCallback(
    (e) => {
      setInternalChecked(e);
      onChange(e);
    },
    [onChange],
  );

  return (
    <>
      <div className={classNames} id={id} data-qa-id="checkbox-field">
        <Checkbox
          className="checkbox-field__input"
          checked={checked}
          context={context}
          disabled={disabled}
          icon={icon}
          id={`${id}-checkbox`}
          name={name}
          onBlur={onBlur}
          onChange={handleChange}
          readOnly={readOnly}
          required={required}
          title={labelContent}
          type={type}
          value={value}
        />
        <HtmlElement
          className="checkbox-field__label"
          decoration={decoration}
          id={`${id}-label`}
          font={font}
          htmlFor={`${id}-checkbox-input`}
          letterSpacing={letterSpacing}
          lineHeight={lineHeight}
          onClick={labelOnClick}
          size={size}
          tag="label"
          textTransform={textTransform}
          title={title}
          weight={weight}
        >
          {labelContent}
        </HtmlElement>
      </div>
      {errors && <FieldErrors name={name} errors={errors} icon={errorsIcon} />}
    </>
  );
};

CheckboxField.defaultProps = {
  checked: false,
  className: '',
  context: null,
  decoration: 'none',
  disabled: false,
  errors: null,
  errorsIcon: null,
  font: 'default',
  icon: null,
  id: null,
  labelContent: '',
  labelOnClick: () => {},
  letterSpacing: 'normal',
  lineHeight: 'default',
  name: '',
  onBlur: () => {},
  onChange: () => {},
  position: 'left',
  readOnly: false,
  required: false,
  size: 'medium',
  textTransform: 'none',
  title: '',
  type: 'checkbox',
  value: '',
  weight: 'normal',
};

CheckboxField.propTypes = {
  checked: PropTypes.bool,
  className: PropTypes.string,
  context: PropTypes.string,
  decoration: PropTypes.oneOf(['none', 'underline']),
  disabled: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.string),
  errorsIcon: PropTypes.element,
  font: PropTypes.oneOf(['default', 'primary', 'secondary']),
  icon: PropTypes.element,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labelContent: PropTypes.string,
  labelOnClick: PropTypes.func,
  letterSpacing: PropTypes.oneOf(['small', 'normal', 'medium', 'large']),
  lineHeight: PropTypes.oneOf(['small', 'default', 'large']),
  name: PropTypes.string,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  position: PropTypes.oneOf(['right', 'left']),
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  size: PropTypes.oneOf(['x-small', 'small', 'medium', 'large']),
  textTransform: PropTypes.oneOf([
    'none',
    'lowercase',
    'uppercase',
    'capitalize',
  ]),
  title: PropTypes.string,
  type: PropTypes.oneOf(['checkbox', 'toggle']),
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  weight: PropTypes.oneOf(['light', 'normal', 'bold', 'extrabold']),
};

export default CheckboxField;
